import { useCallback } from 'react';
import Featurebase, {
  clearConfig,
  shutdown,
  update as updateFeaturebase,
  whenReady,
  type FeaturebaseSettings
} from 'featurebase-js';
import type User from '@global-interfaces/User';

type BootStatus = 'idle' | 'pending' | 'ready';

const BOOT_READY_TIMEOUT_MS = 10_000;

let bootStatus: BootStatus = 'idle';
let bootGeneration = 0;
let bootReadyTimer: ReturnType<typeof setTimeout> | null = null;
let latestSettings: FeaturebaseSettings | null = null;

const clearBootReadyTimer = () => {
  if (!bootReadyTimer) {
    return;
  }

  clearTimeout(bootReadyTimer);
  bootReadyTimer = null;
};

const resetFeaturebaseSdk = ({ keepSettings = false } = {}) => {
  shutdown();
  clearConfig();
  clearBootReadyTimer();
  bootGeneration += 1;
  bootStatus = 'idle';

  if (!keepSettings) {
    latestSettings = null;
  }
};

const markBootPending = () => {
  bootGeneration += 1;
  const currentBootGeneration = bootGeneration;
  bootStatus = 'pending';
  clearBootReadyTimer();
  bootReadyTimer = setTimeout(() => {
    bootReadyTimer = null;

    if (bootGeneration === currentBootGeneration && bootStatus === 'pending') {
      const retrySettings = latestSettings;
      resetFeaturebaseSdk({ keepSettings: true });

      if (retrySettings) {
        bootFeaturebase(retrySettings);
      }
    }
  }, BOOT_READY_TIMEOUT_MS);

  whenReady(() => {
    if (bootGeneration !== currentBootGeneration) {
      return;
    }

    bootStatus = 'ready';
    clearBootReadyTimer();
  });
};

const bootFeaturebase = (settings: FeaturebaseSettings) => {
  Featurebase(settings);
  markBootPending();
};

const getFeaturebaseSettings = (user: User): FeaturebaseSettings | null => {
  const appId = import.meta.env.VITE_FEATUREBASE_APP_ID;

  if (!appId || !user.featurebase_jwt) {
    return null;
  }

  return {
    appId,
    featurebaseJwt: user.featurebase_jwt,
    alignment: 'left'
  };
};

export const useFeaturebase = () => {
  const update = useCallback((user: User) => {
    const settings = getFeaturebaseSettings(user);

    if (!settings) {
      resetFeaturebaseSdk();
      return;
    }

    latestSettings = settings;

    if (bootStatus === 'ready') {
      updateFeaturebase(settings);
      return;
    }

    if (bootStatus === 'pending') {
      return;
    }

    bootFeaturebase(settings);
  }, []);

  const refresh = useCallback((user: User) => {
    const settings = getFeaturebaseSettings(user);

    if (!settings) {
      resetFeaturebaseSdk();
      return;
    }

    latestSettings = settings;

    if (bootStatus === 'ready') {
      updateFeaturebase(settings);
      return;
    }

    if (bootStatus === 'pending') {
      return;
    }

    bootFeaturebase(settings);
  }, []);

  const reset = useCallback(() => {
    resetFeaturebaseSdk();
  }, []);

  return { refresh, reset, update };
};
